import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isToday from 'dayjs/plugin/isToday';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';

type DatePropertyType = Date | string | number | null;

dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(isToday);
dayjs.extend(isoWeek);
dayjs.extend(timezone);

const getDayJSObject = (date?: DatePropertyType) =>
  date ? dayjs(date) : dayjs();

const getDateObject = (date?: DatePropertyType) => new Date(date ?? Date.now());

const getDate = (date?: DatePropertyType) => getDateObject(date).getDate();

const getTime = (date?: DatePropertyType) => getDateObject(date).getTime();

const getValueOf = (date?: DatePropertyType) =>
  date ? getDateObject(date).valueOf() : null;

const getDiff = (dateA: DatePropertyType, dateB?: DatePropertyType) =>
  getDayJSObject(dateA).diff(getDayJSObject(dateB));

const getDaysDifference = (date1: number, date2: number) => {
  return dayjs(date1).diff(dayjs(date2), 'day');
};

const getEndOfDayDateObject = (date?: DatePropertyType) =>
  getDayJSObject(date).endOf('day').toDate();

const getStartOfDayDateObject = (date?: DatePropertyType) =>
  getDayJSObject(date).startOf('day').toDate();

const getPrevMonthDateObject = (date?: DatePropertyType) =>
  getDayJSObject(date).subtract(1, 'month').toDate();

const getUTCDateObject = (date?: DatePropertyType) =>
  getDayJSObject(date).utc().toDate();

const getFormattedDate = (date?: DatePropertyType, template?: string) =>
  getDayJSObject(date).format(template);

const getDateShortMonth = (date?: DatePropertyType) =>
  getDateObject(date).toLocaleString('en-us', { month: 'short' });

const getDateOrdinal = (date: DatePropertyType): string =>
  getDayJSObject(date)
    .format('Do')
    .split('')
    .filter((item) => isNaN(Number(item)))
    .join('');

const getDateToDisplay = (date: DatePropertyType) =>
  getFormattedDate(date, 'MMM Do');

const getTimeToDisplay = (date: DatePropertyType) =>
  getFormattedDate(date, 'h:mm a');

const dateIsToday = (date: DatePropertyType) => getDayJSObject(date).isToday();

const dateIsEndOfDay = (date: DatePropertyType) =>
  getTime(date) === getTime(getEndOfDayDateObject(date));

const getScheduledStartDate = (date: DatePropertyType) => {
  if (!date) return {};

  return {
    formattedStartDate: getFormattedDate(date, 'MMM DD, h:mm a'),
    localeStartDate: getFormattedDate(date, 'YYYY-MM-DD hh:mm:sss'),
  };
};

const getScheduledDueDate = (date: DatePropertyType) => {
  if (!date) return {};

  return {
    formattedDueDate: getFormattedDate(date, 'MM/DD/YYYY, h:mm a'),
    localeDueDate: getFormattedDate(date, 'YYYY-MM-DD hh:mm:sss'),
  };
};

const getHasDayChanged = (
  prevDate: number | null | undefined,
  currentDate: number | null | undefined,
) => {
  if (!prevDate || !currentDate) {
    return Boolean((!prevDate && currentDate) || (prevDate && !currentDate));
  }

  const dateDayJSObject = dateService.getDayJSObject(currentDate);

  return dateDayJSObject.diff(prevDate, 'day') !== 0;
};

/**
 * Compares two timestamps as an ISO strings.
 * Takes into account day period change (AM/PM).
 * */
const getHasTimeChanged = (
  prevTime: number | null | undefined,
  currentTime: number | null | undefined,
) => {
  if (!prevTime || !currentTime) {
    return Boolean((!prevTime && currentTime) || (prevTime && !currentTime));
  }

  const timeDayJSObject = dateService.getDayJSObject(currentTime);
  const prevTimeDayJSObject = dateService.getDayJSObject(prevTime);

  // when change from AM to PM and vise versa
  if (Math.abs(timeDayJSObject.diff(prevTimeDayJSObject, 'hour')) === 12) {
    return true;
  }

  const timeIsoString = timeDayJSObject.format('h-m-s');
  const prevTimeIsoString = prevTimeDayJSObject.format('h-m-s');

  return timeIsoString !== prevTimeIsoString;
};

const getStartOfIsoWeekTimestamp = (date: DatePropertyType) => {
  const startOfWeek = getDayJSObject(date).isoWeekday(1).startOf('day');

  return startOfWeek.valueOf();
};

export const dateService = {
  getDayJSObject,
  getDateObject,
  getDate,
  getTime,
  getValueOf,
  getDiff,
  getDaysDifference,
  getEndOfDayDateObject,
  getStartOfDayDateObject,
  getPrevMonthDateObject,
  getUTCDateObject,
  getFormattedDate,
  getDateShortMonth,
  getDateOrdinal,
  getDateToDisplay,
  getTimeToDisplay,
  dateIsToday,
  dateIsEndOfDay,
  getScheduledStartDate,
  getScheduledDueDate,
  getHasDayChanged,
  getHasTimeChanged,
  getStartOfIsoWeekTimestamp,
};
